import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { Editor, Range } from '@tiptap/react';
import type {
  SuggestionMenuProps,
  SuggestionMenuRenderProps,
  SuggestionItem,
} from '../../ui/tiptap/tiptap-ui-utils/suggestion-menu';
import { SuggestionMenu } from '../../ui/tiptap/tiptap-ui-utils/suggestion-menu';
import type { MentionOption, MentionGroup } from '../../ui/tiptap/extensions';
import type { ResourcePickerItem } from '../types';
import { useResourcePickerState } from '../use-resource-picker-state';
import { ResourcePickerContentControlled } from '../resource-picker-content';
import {
  mentionOptionToPickerItem,
  buildSubgroupIconMap,
  buildCategoryIconMap,
} from '../utils/mention-option-adapter';
import { createResourcePickerFilter } from '../utils/resource-picker-fuzzy-search';

// ─── Width ──────────────────────────────────────────────────────────

const PICKER_WIDTH_PX = 360;

/**
 * Max height for the floating mention picker. Clamped to a fixed 400px, a
 * fraction of the viewport (72vh), and — crucially — the space that
 * SuggestionMenu's floating-ui `size` middleware measured as actually available
 * above/below the caret (exposed via `--suggestion-menu-max-height`). Without
 * that last term the menu renders at its full height even when there's less room
 * above the caret, so it runs off the top of the page instead of scrolling.
 */
const PICKER_MAX_HEIGHT =
  'min(400px, 72vh, var(--suggestion-menu-max-height, 400px))';

// ─── Props ──────────────────────────────────────────────────────────

export type MentionResourcePickerProps<T> = Omit<
  SuggestionMenuProps,
  'items' | 'children'
> & {
  options: MentionOption<T>[];
  groups?: MentionGroup[];
  isLoading?: boolean;
  /**
   * Renders the picker's own search box. Off by default — the `@` query already
   * filters this menu, and a focused input would take the caret off the editor.
   */
  enableSearch?: boolean;
  getOptionId?: (option: MentionOption<T>) => string;
  getOptionLabel?: (option: MentionOption<T>) => string;
  /**
   * When this returns true, the default mention insertion is skipped (caller
   * handled it, e.g. opened a dialog for Coco input mentions).
   */
  interceptMentionInsert?: (props: {
    editor: Editor;
    range: Range;
    context?: MentionOption<T>;
  }) => boolean;
  /**
   * Opens the full tools catalogue. Without it the picker has no way out to
   * everything that was not surfaced here.
   */
  onBrowseAll?: () => void;
};

// ─── Component ──────────────────────────────────────────────────────

export const MentionResourcePicker = <T,>({
  options,
  groups,
  isLoading = false,
  enableSearch = false,
  getOptionId,
  getOptionLabel,
  interceptMentionInsert,
  onBrowseAll,
  ...restProps
}: MentionResourcePickerProps<T>): React.ReactElement => {
  // ── Convert options for the picker UI ──
  const subgroupIcons = useMemo(() => buildSubgroupIconMap(groups), [groups]);

  const pickerItems = useMemo(
    () => options.map((opt) => mentionOptionToPickerItem(opt)),
    [options]
  );

  const filterPickerItems = useMemo(
    () => createResourcePickerFilter(pickerItems),
    [pickerItems]
  );

  const categoryIcons = useMemo(() => buildCategoryIconMap(groups), [groups]);

  // ── id → original MentionOption lookup ──
  const optionById = useMemo(() => {
    const map = new Map<string, MentionOption<T>>();
    for (const opt of options) {
      const id = opt.referenceId ?? opt.label;
      map.set(id, opt);
    }
    return map;
  }, [options]);

  // ── State ──
  const picker = useResourcePickerState({
    items: pickerItems,
    mode: 'single',
    categoryIcons,
    providerIcons: subgroupIcons,
    // Presence of the handler decides whether the row exists, so the row and
    // its action cannot drift apart.
    browseAllLabel: onBrowseAll !== undefined ? 'Browse all tools' : undefined,
  });

  // ── Refs for bridging picker selection → SuggestionMenu command flow ──
  // When the picker selects an item, we call `onSelectRef.current(suggestionItem)`
  // which flows through SuggestionMenu → Suggestion plugin command → onSelect
  // callback on the SuggestionItem, which gets the correct editor + range.
  const onSuggestionSelectRef = useRef<
    ((item: SuggestionItem<MentionOption<T>>) => void) | null
  >(null);

  const getOptionIdRef = useRef(getOptionId);
  getOptionIdRef.current = getOptionId;
  const getOptionLabelRef = useRef(getOptionLabel);
  getOptionLabelRef.current = getOptionLabel;
  const interceptRef = useRef(interceptMentionInsert);
  interceptRef.current = interceptMentionInsert;
  const optionByIdRef = useRef(optionById);
  optionByIdRef.current = optionById;

  const buildMentionContent = useCallback((pickerItemId: string) => {
    const original = optionByIdRef.current.get(pickerItemId);
    const id =
      getOptionIdRef.current !== undefined && original !== undefined
        ? getOptionIdRef.current(original)
        : String(original?.label ?? pickerItemId);
    const label =
      getOptionLabelRef.current !== undefined && original !== undefined
        ? getOptionLabelRef.current(original)
        : original?.label ?? pickerItemId;

    return [
      {
        type: 'mention',
        attrs: {
          id,
          label,
          referenceId: pickerItemId,
          ...(original?.color !== undefined && { color: original.color }),
          data:
            original?.data !== undefined
              ? JSON.stringify(original.data)
              : undefined,
        },
      },
      {
        type: 'text',
        text: ' ',
      },
    ];
  }, []);

  /**
   * Build the `onSelect` callback for a SuggestionItem. This is called by the
   * Suggestion plugin's `command` handler, which provides the correct editor
   * and range (including the `@` trigger text).
   */
  const makeItemOnSelect = useCallback(
    (pickerItemId: string) =>
      (props: { editor: Editor; range: Range; context?: MentionOption<T> }) => {
        if (props.editor.isDestroyed) {
          return;
        }

        const original = optionByIdRef.current.get(pickerItemId);

        // Check intercept (e.g. Coco input mentions open a dialog).
        if (
          interceptRef.current !== undefined &&
          original !== undefined &&
          interceptRef.current({
            editor: props.editor,
            range: props.range,
            context: original,
          }) === true
        ) {
          return;
        }

        props.editor
          .chain()
          .focus()
          .insertContentAt(props.range, buildMentionContent(pickerItemId))
          .run();
      },
    [buildMentionContent]
  );

  const makeItemsOnSelect = useCallback(
    (pickerItemIds: string[]) =>
      (props: { editor: Editor; range: Range; context?: MentionOption<T> }) => {
        if (props.editor.isDestroyed) {
          return;
        }

        const content = pickerItemIds.flatMap((pickerItemId) =>
          buildMentionContent(pickerItemId)
        );

        props.editor
          .chain()
          .focus()
          .insertContentAt(props.range, content)
          .run();
      },
    [buildMentionContent]
  );

  /**
   * Called when the picker UI selects an item. Creates a SuggestionItem on the
   * fly and calls the SuggestionMenu's onSelect, which flows through the
   * Suggestion plugin's command handler to get the correct range.
   */
  const handlePickerSelect = useCallback(
    (item: ResourcePickerItem<T>) => {
      const selectFn = onSuggestionSelectRef.current;
      if (selectFn === null) {
        return;
      }

      const original = optionById.get(item.id);
      const suggestionItem: SuggestionItem<MentionOption<T>> = {
        title: item.label,
        context: original,
        onSelect: makeItemOnSelect(item.id),
      };

      selectFn(suggestionItem);
    },
    [optionById, makeItemOnSelect]
  );

  const handlePickerMultiConfirm = useCallback(
    (items: ResourcePickerItem<T>[]) => {
      const selectFn = onSuggestionSelectRef.current;
      if (selectFn === null || items.length === 0) {
        return;
      }

      if (items.length === 1) {
        const item = items[0];
        if (item !== undefined) {
          handlePickerSelect(item);
        }
        return;
      }

      const pickerItemIds = items.map((item) => item.id);
      const suggestionItem: SuggestionItem<MentionOption<T>> = {
        title: items.map((item) => item.label).join(', '),
        context: undefined,
        onSelect: makeItemsOnSelect(pickerItemIds),
      };

      selectFn(suggestionItem);
      picker.clearSelection();
    },
    [handlePickerSelect, makeItemsOnSelect, picker]
  );

  const hasPickerMatches = useCallback(
    (query: string): boolean => {
      if (isLoading) {
        return true;
      }
      if (options.length === 0) {
        return false;
      }
      if (query.trim().length === 0) {
        return true;
      }
      return filterPickerItems(query).length > 0;
    },
    [filterPickerItems, isLoading, options.length]
  );

  const placeholderSuggestionItem = useMemo(
    (): SuggestionItem<MentionOption<T>> => ({
      title: '__resource_picker__',
      onSelect: () => {},
    }),
    []
  );

  // ── Items callback for SuggestionMenu (controls show/hide) ──
  const getSuggestionItems = useCallback(
    async (props: {
      query: string;
    }): Promise<SuggestionItem<MentionOption<T>>[]> => {
      if (!hasPickerMatches(props.query)) {
        return [];
      }
      // Return a single dummy item so the plugin knows to show the menu.
      // The picker renders its own list; this just gates visibility.
      return [placeholderSuggestionItem];
    },
    [hasPickerMatches, placeholderSuggestionItem]
  );

  const itemsOverrideFn = useCallback(
    (query: string): SuggestionItem<MentionOption<T>>[] => {
      if (!hasPickerMatches(query)) {
        return [];
      }
      return [placeholderSuggestionItem];
    },
    [hasPickerMatches, placeholderSuggestionItem]
  );

  // ── Keyboard handling ──
  const handleBeforeKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      const consume = (): true => {
        event.preventDefault();
        event.stopPropagation();
        return true;
      };

      const focusedRow = picker.visibleRows.find(
        (row) =>
          (row.kind === 'item' || row.kind === 'provider-trigger') &&
          row.navIndex === picker.cursorIndex
      );

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp':
        case 'Tab': {
          if (picker.handleKeyDown(event)) {
            return consume();
          }
          return false;
        }
        case 'ArrowRight': {
          if (focusedRow === undefined) {
            return false;
          }
          if (focusedRow.kind === 'provider-trigger' && !focusedRow.expanded) {
            picker.toggleProvider(focusedRow.provider);
            return consume();
          }
          if (
            focusedRow.kind === 'item' &&
            focusedRow.item.detail !== undefined
          ) {
            picker.setExpandedItem(focusedRow.item.id);
            return consume();
          }
          return false;
        }
        case 'ArrowLeft': {
          if (picker.expandedItemId !== undefined) {
            picker.setExpandedItem(undefined);
            return consume();
          }
          if (focusedRow === undefined) {
            return false;
          }
          if (
            focusedRow.kind === 'item' &&
            focusedRow.indented === true &&
            focusedRow.item.provider !== undefined
          ) {
            picker.toggleProvider(focusedRow.item.provider);
            return consume();
          }
          if (focusedRow.kind === 'provider-trigger' && focusedRow.expanded) {
            picker.toggleProvider(focusedRow.provider);
            return consume();
          }
          return false;
        }
        case 'Enter': {
          if (event.isComposing || focusedRow === undefined) {
            return false;
          }
          if (focusedRow.kind === 'provider-trigger') {
            picker.toggleProvider(focusedRow.provider);
            return consume();
          }
          if (focusedRow.kind === 'item') {
            handlePickerSelect(focusedRow.item);
            return consume();
          }
          return false;
        }
        default:
          return false;
      }
    },
    [handlePickerSelect, picker]
  );

  // ── Track open/close ──
  const prevShowRef = useRef(false);

  return (
    <SuggestionMenu
      char="@"
      allowSpaces={true}
      pluginKey="mentionDropdownMenu"
      decorationClass="tiptap-mention-decoration"
      selector="tiptap-mention-dropdown-menu"
      items={getSuggestionItems}
      itemsOverrideFn={itemsOverrideFn}
      beforeKeyDown={handleBeforeKeyDown}
      {...restProps}
    >
      {(renderProps: SuggestionMenuRenderProps<MentionOption<T>>) => {
        const isShowing = renderProps.items.length > 0;

        // Capture the SuggestionMenu's onSelect so we can call it from the picker.
        onSuggestionSelectRef.current = isShowing ? renderProps.onSelect : null;

        // Reset picker when the popup opens.
        if (isShowing && !prevShowRef.current) {
          prevShowRef.current = true;
          picker.reset();
        } else if (!isShowing) {
          prevShowRef.current = false;
        }

        if (!isShowing) {
          return null;
        }

        return (
          <ResourcePickerContentControlled
            picker={picker}
            mode="single"
            onSelect={handlePickerSelect}
            onMultiConfirm={handlePickerMultiConfirm}
            onDismiss={() => {
              renderProps.closeMenu?.();
              // Re-focus the editor so the cursor is visible after dismissing.
              renderProps.editor?.commands.focus();
            }}
            isLoading={isLoading}
            onBrowseAll={onBrowseAll}
            width={PICKER_WIDTH_PX}
            maxHeight={PICKER_MAX_HEIGHT}
            searchQuery={renderProps.query ?? ''}
            showSearchInput={enableSearch}
          />
        );
      }}
    </SuggestionMenu>
  );
};
